import React from 'react'
import {
    Box,
    Button,
    HStack,
    IconButton,
    Text,
    Input,
    Select,
    useBreakpointValue, InputGroup, InputRightElement, Flex,
} from '@chakra-ui/react'
import {ChevronLeftIcon, ChevronRightIcon} from '@chakra-ui/icons'
import { useTranslation } from 'react-i18next'

const PER_PAGE_OPTIONS = [15, 25, 50, 100]

const Pagination = ({currentPage, totalPages, onPageChange, perPage, onPerPageChange}) => {
    const { i18n } = useTranslation()
    const l = (en, ro) => (i18n.language === 'ro' ? ro : en)
    const isFirstPage = currentPage === 1
    const isLastPage = currentPage === totalPages
    const gap = useBreakpointValue({base: 1, md: 2})
    const btnSize = useBreakpointValue({base: 'xs', md: 'sm'})

    const handlePreviousPage = () => !isFirstPage && onPageChange(currentPage - 1)
    const handleNextPage = () => !isLastPage && onPageChange(currentPage + 1)

    const handlePageInputChange = (e) => {
        const v = parseInt(e.target.value, 10)
        if (v >= 1 && v <= totalPages) onPageChange(v)
    }

    const renderPageNumbers = () => {
        const numbers = []
        const startPage = Math.max(currentPage - 2, 1)
        const endPage = Math.min(currentPage + 2, totalPages)
        for (let i = startPage; i <= endPage; i++) {
            numbers.push(
                <Button
                    key={i}
                    size={btnSize}
                    onClick={() => onPageChange(i)}
                    variant={i === currentPage ? 'solid' : 'ghost'}
                    colorScheme="blue"
                    px={2}
                    minW="32px"
                >
                    {i}
                </Button>
            )
        }
        return numbers
    }

    return (
        <Box
            mt={4}
            w="full"
            display="flex"
            flexWrap="wrap"
            justifyContent="center"
            alignItems="center"
            gap={gap}
        >
            <IconButton
                size={btnSize}
                icon={<ChevronLeftIcon/>}
                aria-label={l('Previous page', 'Pagina anterioara')}
                onClick={handlePreviousPage}
                isDisabled={isFirstPage}
                variant="ghost"
            />

            <HStack spacing={gap}>{renderPageNumbers()}</HStack>

            <IconButton
                size={btnSize}
                icon={<ChevronRightIcon/>}
                aria-label={l('Next page', 'Pagina urmatoare')}
                onClick={handleNextPage}
                isDisabled={isLastPage}
                variant="ghost"
            />

            <Flex
                display={{ base: 'none', sm: 'flex' }}
                align="center"
                gap={2}
                ml={gap}
                fontSize="xs"
            >
                <Text whiteSpace="nowrap">{l('Go to', 'Mergi la')}</Text>
                <InputGroup size="xs" w="80px">
                    <Input
                        type="number"
                        min="1"
                        max={totalPages}
                        value={currentPage}
                        onChange={handlePageInputChange}
                        textAlign="center"
                        pr="45px"
                        sx={{
                            '::-webkit-outer-spin-button': { display: 'none' },
                            '::-webkit-inner-spin-button': { display: 'none' },
                        }}
                    />
                    <InputRightElement
                        pointerEvents="none"
                        width="45px"
                        children={<Text color="gray.500">/ {totalPages}</Text>}
                    />
                </InputGroup>
            </Flex>

            {onPerPageChange && (
                <Flex align="center" gap={2} ml={gap} fontSize="xs">
                    <Text whiteSpace="nowrap">{l('Per page', 'Pe pagina')}</Text>
                    <Select
                        size="xs"
                        w="70px"
                        borderRadius="md"
                        value={perPage || PER_PAGE_OPTIONS[0]}
                        onChange={(e) => onPerPageChange(parseInt(e.target.value, 10))}
                    >
                        {PER_PAGE_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </Select>
                </Flex>
            )}
        </Box>
    )
}

export default Pagination
